<template>
  <div class="admin-page">
    <Transition>
      <TopBanner :mode="bannerMode" :text="bannerText" v-if="bannerText" />
    </Transition>

    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div class="admin-page-heading">
          <button
            type="button"
            class="admin-button--secondary admin-button--sm"
            @click="goBack">
            <i class="fa fa-arrow-left mr-2" aria-hidden="true"></i>Zurück
          </button>
          <div>
            <span class="admin-kicker">Datenverwaltung</span>
            <h1 class="admin-page-title">Zugangscodes</h1>
          </div>
        </div>
        <div class="admin-action-bar">
          <button
            type="button"
            class="admin-button--primary"
            @click="showAddModal = true">
            <i class="fa fa-plus mr-2"></i>Code hinzufügen
          </button>
        </div>
      </div>

      <div v-if="contextInfo" class="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg inline-block">
        <p class="font-semibold text-blue-800">
          <i class="fa fa-info-circle mr-2"></i>{{ contextInfo }}
        </p>
      </div>

      <section class="admin-panel">
        <div class="admin-panel__body overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-200">
              <th class="border px-4 py-2">Code</th>
              <th class="border px-4 py-2">Beschreibung</th>
              <th class="border px-4 py-2">Vertrag</th>
              <th class="border px-4 py-2">Einsatzort</th>
              <th class="border px-4 py-2">Einsatzstelle</th>
              <th class="border px-4 py-2">Gültig von</th>
              <th class="border px-4 py-2">Gültig bis</th>
              <th class="border px-4 py-2">Status</th>
              <th class="border px-4 py-2">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="code in accessCodes" :key="code.id" class="hover:bg-gray-50">
              <td class="border px-4 py-2 font-mono font-semibold">{{ code.code }}</td>
              <td class="border px-4 py-2">{{ code.description }}</td>
              <td class="border px-4 py-2">{{ code.contractName }}</td>
              <td class="border px-4 py-2">{{ code.locationName }}</td>
              <td class="border px-4 py-2">{{ code.placeName }}</td>
              <td class="border px-4 py-2">{{ formatDate(code.validFrom) }}</td>
              <td class="border px-4 py-2">{{ formatDate(code.validUntil) }}</td>
              <td class="border px-4 py-2">
                <span :class="code.active ? 'px-2 py-1 rounded bg-green-200 text-green-800' : 'px-2 py-1 rounded bg-red-200 text-red-800'">
                  {{ code.active ? 'Aktiv' : 'Inaktiv' }}
                </span>
              </td>
              <td class="border px-4 py-2">
                <button type="button" @click="editCode(code)" class="admin-button--ghost admin-button--icon admin-button--sm mr-2" aria-label="Bearbeiten">
                  <i class="fa fa-edit"></i>
                </button>
                <button type="button" @click="deleteCode(code)" class="admin-button--danger admin-button--icon admin-button--sm" aria-label="Löschen">
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="accessCodes.length === 0">
              <td colspan="9" class="border px-4 py-8 text-gray-500">
                Keine Zugangscodes gefunden
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </section>
    </div>

    <AccessCodeModal 
      v-if="showAddModal || editingCode"
      :code="editingCode"
      :defaultContext="defaultContext"
      @close="closeModal"
      @save="saveCode"
    />
  </div>
</template>

<script>
import CASP from "../../CASP";
import TopBanner from "@/components/TopBanner";
import AccessCodeModal from "@/components/AccessCodeModal.vue";

export default {
  name: "AdminAccessCodes",
  components: { TopBanner, AccessCodeModal },
  data() {
    return {
      accessCodes: [],
      showAddModal: false,
      editingCode: null,
      bannerText: '',
      bannerMode: 'success',
      defaultContext: {}
    };
  },
  computed: {
    contextInfo() {
      const query = this.$route.query;
      if (query.placeName) {
        return `${query.contractName} → ${query.locationName} → ${query.placeName}`;
      }
      return null;
    }
  },
  methods: {
    loadAccessCodes() {
      document.getElementById("mainspinner").style.display = "inline-block";
      const filters = {};
      
      const query = this.$route.query;
      if (query.contractId) filters.contractId = query.contractId;
      if (query.locationId) filters.locationId = query.locationId;
      if (query.placeId) filters.placeId = query.placeId;
      
      CASP.getAccessCodes(filters, (status, response) => {
        document.getElementById("mainspinner").style.display = "none";
        if (status === 200) {
          this.accessCodes = response;
        }
      });
    },
    editCode(code) {
      this.editingCode = { ...code };
    },
    deleteCode(code) {
      if (confirm('Möchten Sie diesen Zugangscode wirklich löschen?')) {
        document.getElementById("mainspinner").style.display = "inline-block";
        CASP.deleteAccessCode(code.id, (status) => {
          document.getElementById("mainspinner").style.display = "none";
          if (status === 200) {
            this.bannerMode = 'success';
            this.bannerText = 'Zugangscode erfolgreich gelöscht';
            this.loadAccessCodes();
          } else {
            this.bannerMode = 'error';
            this.bannerText = 'Fehler beim Löschen des Zugangscodes';
          }
          setTimeout(() => { this.bannerText = ''; }, 3000);
        });
      }
    },
    closeModal() {
      this.showAddModal = false;
      this.editingCode = null;
    },
    saveCode(code) {
      this.loadAccessCodes();
      this.bannerMode = 'success';
      this.bannerText = code.id ? 'Zugangscode aktualisiert' : 'Zugangscode erfolgreich hinzugefügt';
      setTimeout(() => { this.bannerText = ''; }, 3000);
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('de-DE');
    },
    goBack() {
      if (this.$route.query.contractId) {
        this.$router.push({
          name: 'AdminDataContractDetails',
          params: { id: this.$route.query.contractId },
        });
      } else {
        this.$router.push({ name: 'AdminDataVertraege' });
      }
    }
  },
  mounted() {
    document.getElementById("mainspinner").style.display = "none";
    
    const query = this.$route.query;
    if (query.contractId) {
      this.defaultContext = {
        contractId: query.contractId,
        contractName: query.contractName,
        locationId: query.locationId,
        locationName: query.locationName,
        placeId: query.placeId,
        placeName: query.placeName
      };
    }
    
    this.loadAccessCodes();
  }
};
</script>

<style scoped>
</style>
